import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { Loader2, Monitor } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import { useHedgeSocket } from "@/hooks/use-hedge-socket";
import { usePresencePinger } from "@/hooks/use-presence-pinger";
import { useDashboardStore } from "@/store/dashboard.store";
import type { AccountLive, PositionLive } from "@/store/dashboard.store";
import { getPair, type PairListItem } from "@/lib/api/pairs";
import { getAccountPair } from "@/lib/api/account-pairs";
import { Watchlist } from "@/components/terminal/watchlist";
import { OrderPanel } from "@/components/terminal/order-panel";
import {
  AccountsRail,
  type AccountSelection,
} from "@/components/terminal/accounts-rail";
import { PendingOrdersTable } from "@/components/terminal/pending-orders-table";
import { PositionsMiniTable } from "@/components/terminal/positions-mini-table";
import { PairRail, usePairsList } from "@/components/terminal/pair-rail";
import { SiblingPositionsStrip } from "@/components/terminal/sibling-positions-strip";
import { useLiveFeed } from "@/hooks/use-live-feed";
import {
  TradingViewFuturesChart,
  type ChartPosition,
} from "@/components/terminal/tradingview-futures-chart";
import { EmaSignalCard } from "@/components/terminal/ema-signal-card";
import { isFuturesPair, terminalPath, type TerminalMarket } from "@/lib/market";
import { FUTURES_ALLOWLIST } from "@/lib/futures-symbols";

/**
 * Trading terminal — top-level dashboard page.
 *
 * Routes:
 *   /terminal           — FX/CFD mode: chart + watchlist + pair picker rail.
 *   /futures/terminal   — futures mode: TradingView futures chart + partner rail.
 *                         Order panel is replaced with a "select a pair" hint.
 *   /terminal/:slug and /futures/terminal/:slug — pair mode. Picking a
 *                         different pair keeps the current market terminal.
 *
 * Layout (lg+):
 *   ┌─────────┬───────────────────────────┬────────────┐
 *   │ Watch-  │ Price chart               │ Pair rail  │
 *   │ list    ├───────────────────────────┤            │
 *   │         │ Order panel               │ + Accounts │
 *   ├─────────┴───────────────────────────┴────────────┤
 *   │ Positions                                         │
 *   │ Pending orders                                    │
 *   └───────────────────────────────────────────────────┘
 *
 * Live data:
 *   - broker-radar (useLiveFeed) → bid/ask watchlist + chart (always on,
 *     even with no pair, so the page renders meaningful state on first
 *     load).
 *   - HedgePair DO WS (useHedgeSocket) → positions, equity ticks,
 *     pending:changed events. Only attaches when a pair is selected.
 *
 * Manual trade actions (execute/pending/close/modify) POST through the
 * Hono manual-trade route so every action writes an audit_log row before
 * the frame leaves the worker.
 */

const DEFAULT_ALLOWLIST = [
  "XAUUSD",
  "EURUSD",
  "GBPUSD",
  "USDJPY",
  "BTCUSD",
  "US30",
];
interface TerminalPageProps {
  market?: TerminalMarket;
}

export function TerminalPage({ market = "fx-cfd" }: TerminalPageProps): ReactNode {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const orderPanelRef = useRef<HTMLDivElement | null>(null);

  // `undefined` = loading initial pair, `null` = no pair (or not found),
  // PairListItem = loaded.
  const [pair, setPair] = useState<PairListItem | null | undefined>(
    slug ? undefined : null,
  );
  const defaultAllowlist = market === "futures" ? FUTURES_ALLOWLIST : DEFAULT_ALLOWLIST;
  const [allowlist, setAllowlist] = useState<string[]>(defaultAllowlist);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Right-rail pair list. Always loaded so the operator can switch
  // pairs from anywhere on the page.
  const { pairs: allPairs, loading: pairsLoading } = usePairsList(market);

  // Resolve the URL slug → pair, or fall back to no-pair mode.
  useEffect(() => {
    if (!slug) {
      setPair(null);
      setAllowlist(defaultAllowlist);
      setLoadError(null);
      return;
    }
    let cancelled = false;
    setPair(undefined);
    setLoadError(null);
    (async () => {
      try {
        const p = await getPair(slug);
        if (cancelled) return;
        if (p && isFuturesPair(p) !== (market === "futures")) {
          setPair(null);
          setAllowlist(defaultAllowlist);
          setLoadError(
            market === "futures"
              ? "That pair belongs to the FX/CFD terminal."
              : "That pair belongs to the futures terminal.",
          );
          return;
        }
        setPair(p);
        if (p?.accountPairSlug) {
          const ap = await getAccountPair(p.accountPairSlug).catch(() => null);
          if (cancelled) return;
          const fromApi = ap?.accountPair.symbolAllowlist ?? [];
          const merged = Array.from(
            new Set([
              p.symbol.toUpperCase(),
              ...fromApi.map((s) => s.toUpperCase()),
            ]),
          );
          setAllowlist(merged);
        } else if (p) {
          setAllowlist([p.symbol.toUpperCase()]);
        } else {
          // Slug provided but pair not found — still show meaningful UI.
          setAllowlist(defaultAllowlist);
        }
      } catch (e) {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : String(e));
        setPair(null);
        setAllowlist(defaultAllowlist);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, market, defaultAllowlist]);

  // WS attaches only when a real pair is loaded (hook is null-safe).
  useHedgeSocket(pair?.id ?? null);

  // Keep the parent account_pair awake while the operator is on the
  // terminal. The pinger sends the pair slug; server resolves to the
  // account_pair and stamps a fresh idle deadline on every ping.
  // Empty array when no pair is selected → no-op.
  usePresencePinger(pair ? [pair.accountPairSlug ?? pair.slug] : []);

  const liveAccounts = useDashboardStore((s) =>
    pair ? (s.pairs[pair.id]?.accounts ?? null) : null,
  );
  const livePositions = useDashboardStore((s) =>
    pair ? (s.pairs[pair.id]?.positions ?? null) : null,
  );
  const activityFeed = useDashboardStore((s) => s.activityFeed);

  const [selection, setSelection] = useState<Map<string, AccountSelection>>(
    () => new Map(),
  );
  // Reset per-leg selection whenever the active pair changes — selection
  // keys reference account ids that are no longer relevant.
  useEffect(() => {
    setSelection(new Map());
  }, [pair?.id]);

  const [selectedSymbol, setSelectedSymbol] = useState<string>(
    pair?.symbol?.toUpperCase() ?? defaultAllowlist[0]!,
  );
  useEffect(() => {
    if (pair?.symbol) setSelectedSymbol(pair.symbol.toUpperCase());
    else setSelectedSymbol((prev) =>
      defaultAllowlist.includes(prev) ? prev : defaultAllowlist[0]!,
    );
  }, [pair?.symbol, defaultAllowlist]);

  const { data: feed } = useLiveFeed("opofinance", "ecn");
  const selectedTick = useMemo(
    () => feed.find((d) => d.symbol.toUpperCase() === selectedSymbol) ?? null,
    [feed, selectedSymbol],
  );

  const refreshPendingRef = useRef<() => Promise<void>>(() =>
    Promise.resolve(),
  );
  useEffect(() => {
    const latest = activityFeed[0];
    if (latest?.type === "pending:changed") {
      void refreshPendingRef.current();
    }
  }, [activityFeed]);

  const onSelectPair = (nextSlug: string): void => {
    if (nextSlug === slug) return;
    navigate(terminalPath(market, nextSlug), { replace: true });
  };

  // Watchlist click. Each symbol on this account-pair has its OWN child
  // pair row (one per /symbols add), so changing the displayed symbol
  // must also switch the URL/pair_id — otherwise the order panel ships
  // pairId=current with symbol=clicked, the trade attributes to the
  // sibling via findPairByAccountAndSymbol, and the user sees "Open On
  // Sibling Pairs". Fall back to local-only when no sibling exists for
  // the symbol (e.g. futures market, or symbol in allowlist but no
  // child row yet).
  const onSelectSymbol = (nextSymbol: string): void => {
    const target = nextSymbol.toUpperCase();
    if (target === selectedSymbol) return;
    if (pair?.accountPairSlug && allPairs) {
      const sibling = allPairs.find(
        (p) =>
          p.accountPairSlug === pair.accountPairSlug &&
          p.symbol.toUpperCase() === target,
      );
      if (sibling && sibling.slug !== slug) {
        navigate(terminalPath(market, sibling.slug), { replace: true });
        return;
      }
    }
    setSelectedSymbol(target);
  };

  const onQuickTrade = (): void => {
    orderPanelRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const isPairLoading = slug !== undefined && pair === undefined;
  // Memoized so derived selectors (chartPositions, SiblingPositionsStrip)
  // don't recompute on every render. New Set per render would otherwise
  // make the chart redraw markers continuously.
  const accountIds = useMemo(
    () =>
      pair
        ? new Set([pair.longAccount.accountId, pair.shortAccount.accountId])
        : new Set<string>(),
    [pair?.longAccount.accountId, pair?.shortAccount.accountId],
  );
  const firmNameFor = (accountId: string): string => {
    if (!pair) return accountId;
    return accountId === pair.longAccount.accountId
      ? pair.longAccount.firmName
      : pair.shortAccount.firmName;
  };

  // Open positions on the active pair on the active symbol — handed to
  // the chart so it can draw entry-price markers + corner chips.
  // Filtering by accountId mirrors PositionsMiniTable so we never spill
  // unrelated accounts' fills onto the chart.
  const chartPositions = useMemo<ChartPosition[]>(() => {
    if (!livePositions) return [];
    const out: ChartPosition[] = [];
    for (const p of Object.values(livePositions)) {
      if (!accountIds.has(p.accountId)) continue;
      if (p.lots <= 0) continue;
      out.push({
        ticket: p.ticket,
        symbol: p.symbol,
        side: p.side,
        lots: p.lots,
        entry: p.entry,
        updatedAt: p.updatedAt,
      });
    }
    return out;
  }, [livePositions, accountIds]);

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Monitor className="size-5 text-accent" />
          <h1 className="font-sans text-lg font-semibold text-foreground">
            Trading <span className="text-accent">Terminal</span>
          </h1>
          <span className="rounded-md bg-accent/10 px-2 py-0.5 text-[10px] font-semibold text-accent">
            {market === "futures" ? "Futures" : "FX & CFD"}
          </span>
          {pair && (
            <span className="truncate font-mono text-xs text-muted-foreground">
              · {pair.longAccount.firmName} ↔ {pair.shortAccount.firmName}
            </span>
          )}
          {!pair && !isPairLoading && (
            <span className="font-mono text-xs text-muted-foreground">
              · Select a Pair to Trade
            </span>
          )}
        </div>
      </motion.div>

      {slug && loadError && (
        <p
          role="alert"
          aria-live="polite"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-mono text-[11px] text-destructive"
        >
          Pair lookup failed: {loadError}
        </p>
      )}
      {slug && !isPairLoading && pair === null && !loadError && (
        <p
          aria-live="polite"
          className="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-600 dark:text-amber-400"
        >
          Pair <span className="font-mono">{slug}</span> not found. Pick another
          from the rail on the right or create a new one.
        </p>
      )}

      {market === "fx-cfd" ? (
        <FxLockerTerminal
          allowlist={allowlist}
          selectedSymbol={selectedSymbol}
          onSelectSymbol={onSelectSymbol}
          selectedTick={selectedTick}
          pair={pair}
          isPairLoading={isPairLoading}
          allPairs={allPairs}
          pairsLoading={pairsLoading}
          selection={selection}
          setSelection={setSelection}
          liveAccounts={liveAccounts}
          livePositions={livePositions}
          accountIds={accountIds}
          firmNameFor={firmNameFor}
          onSelectPair={onSelectPair}
          onQuickTrade={onQuickTrade}
          orderPanelRef={orderPanelRef}
          pairSlug={slug ?? pair?.id ?? null}
          chartPositions={chartPositions}
          onPendingRefreshRef={(fn) => {
            refreshPendingRef.current = fn;
          }}
        />
      ) : (
        <>
          <div
            className={cn(
              "grid min-h-[calc(100vh-9.5rem)] gap-3",
              "grid-cols-1 xl:grid-cols-[250px_minmax(0,1fr)_310px]",
            )}
          >
            <aside className="flex min-h-0 flex-col gap-2">
              <Watchlist
                allowlist={allowlist}
                selected={selectedSymbol}
                onSelect={onSelectSymbol}
                allowUnpricedSelection
                mode="futures"
              />
              <EmaSignalCard symbol={selectedSymbol} />
            </aside>

            <main className="flex min-h-[560px] min-w-0 flex-col gap-3">
              <div className="min-h-[520px] flex-1">
                <TradingViewFuturesChart
                  symbol={selectedSymbol}
                  positions={chartPositions}
                />
              </div>
              <div
                ref={orderPanelRef}
                className="min-h-[180px]"
              >
                <FuturesExecutionPanel pair={pair} />
              </div>
            </main>

            <aside className="flex min-h-0 flex-col gap-3">
              <div className={cn(pair ? "max-h-[260px]" : "flex-1")}>
                <PairRail
                  pairs={allPairs}
                  loading={pairsLoading}
                  selectedAccountPairSlug={pair?.accountPairSlug ?? null}
                  onSelect={onSelectPair}
                  onQuickTrade={pair ? onQuickTrade : undefined}
                  market={market}
                />
              </div>
              {pair && (
                <div className="flex-1">
                  <AccountsRail
                    pair={pair}
                    accountsLive={liveAccounts}
                    selection={selection}
                    onChange={setSelection}
                    market={market}
                  />
                </div>
              )}
            </aside>
          </div>

          {pair ? (
            <>
              <SiblingPositionsStrip
                pair={pair}
                allPairs={allPairs}
                accountIds={accountIds}
                firmNameFor={firmNameFor}
                market={market}
              />
              <PositionsMiniTable
                pairId={pair.id}
                livePositions={livePositions}
                allowedAccountIds={accountIds}
                firmNameFor={firmNameFor}
              />
              <PendingOrdersTable
                pairSlug={slug ?? pair.id}
                onRefRef={(fn) => {
                  refreshPendingRef.current = fn;
                }}
              />
            </>
          ) : (
            <NoPairTablesHint />
          )}
        </>
      )}
    </div>
  );
}

function FxLockerTerminal({
  allowlist,
  selectedSymbol,
  onSelectSymbol,
  selectedTick,
  pair,
  isPairLoading,
  allPairs,
  pairsLoading,
  selection,
  setSelection,
  liveAccounts,
  livePositions,
  accountIds,
  firmNameFor,
  onSelectPair,
  onQuickTrade,
  orderPanelRef,
  pairSlug,
  chartPositions,
  onPendingRefreshRef,
}: {
  allowlist: string[];
  selectedSymbol: string;
  onSelectSymbol(symbol: string): void;
  selectedTick: ReturnType<typeof useLiveFeed>["data"][number] | null;
  pair: PairListItem | null | undefined;
  isPairLoading: boolean;
  allPairs: PairListItem[] | null;
  pairsLoading: boolean;
  selection: Map<string, AccountSelection>;
  setSelection(next: Map<string, AccountSelection>): void;
  liveAccounts: Record<string, AccountLive> | null;
  livePositions: Record<string, PositionLive> | null;
  accountIds: Set<string>;
  firmNameFor(accountId: string): string;
  onSelectPair(slug: string): void;
  onQuickTrade(): void;
  orderPanelRef: React.RefObject<HTMLDivElement | null>;
  pairSlug: string | null;
  chartPositions: ChartPosition[];
  onPendingRefreshRef(fn: () => Promise<void>): void;
}): ReactNode {
  return (
    <>
      <div
        className={cn(
          "grid min-h-[calc(100vh-9.5rem)] gap-3",
          "grid-cols-1 xl:grid-cols-[250px_minmax(0,1fr)_360px]",
        )}
      >
        <aside className="flex min-h-0 flex-col gap-2">
          <Watchlist
            allowlist={allowlist}
            selected={selectedSymbol}
            onSelect={onSelectSymbol}
            allowUnpricedSelection
            mode="fx-cfd"
          />
          <EmaSignalCard symbol={selectedSymbol} />
        </aside>

        <main className="flex min-h-[620px] min-w-0 flex-col gap-3">
          <div className="min-h-[520px] flex-1">
            <TradingViewFuturesChart
              symbol={selectedSymbol}
              market="fx-cfd"
              positions={chartPositions}
            />
          </div>
          <div ref={orderPanelRef} className="min-h-[310px]">
            {pair ? (
              <OrderPanel
                pair={pair}
                symbol={selectedSymbol}
                selection={selection}
                tick={selectedTick}
                livePositions={livePositions}
                onFired={() => {
                  /* Keep market selection; clear pending-selection externally if needed. */
                }}
              />
            ) : (
              <NoPairOrderPanelHint loading={isPairLoading} />
            )}
          </div>
        </main>

        <aside className="flex min-h-0 flex-col gap-3">
          <div className={cn(pair ? "max-h-[300px]" : "flex-1")}>
            <PairRail
              pairs={allPairs}
              loading={pairsLoading}
              selectedAccountPairSlug={pair?.accountPairSlug ?? null}
              onSelect={onSelectPair}
              onQuickTrade={pair ? onQuickTrade : undefined}
              market="fx-cfd"
            />
          </div>
          {pair && (
            <div className="flex-1">
              <AccountsRail
                pair={pair}
                accountsLive={liveAccounts}
                selection={selection}
                onChange={setSelection}
                market="fx-cfd"
              />
            </div>
          )}
        </aside>
      </div>

      {pair ? (
        <>
          <SiblingPositionsStrip
            pair={pair}
            allPairs={allPairs}
            accountIds={accountIds}
            firmNameFor={firmNameFor}
            market="fx-cfd"
          />
          <PositionsMiniTable
            pairId={pair.id}
            livePositions={livePositions}
            allowedAccountIds={accountIds}
            firmNameFor={firmNameFor}
          />
          {pairSlug && (
            <PendingOrdersTable
              pairSlug={pairSlug}
              onRefRef={onPendingRefreshRef}
            />
          )}
        </>
      ) : (
        <NoPairTablesHint />
      )}
    </>
  );
}

function FuturesExecutionPanel({
  pair,
}: {
  pair: PairListItem | null | undefined;
}): ReactNode {
  return (
    <div className="flex h-full min-h-[240px] flex-col gap-3 rounded-xl border border-border bg-card/70 px-4 py-4">
      <div>
        <p className="text-sm font-semibold text-foreground">
          Futures Partner Management
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Futures accounts are paired as hedge partners, not fixed long/short
          legs. Trade direction is decided manually while the dashboard tracks
          which partner is ahead, breached, or ready for replacement.
        </p>
      </div>
      {pair ? (
        <div className="grid gap-2 sm:grid-cols-2">
          <PartnerTile label="Partner A" account={pair.longAccount} />
          <PartnerTile label="Partner B" account={pair.shortAccount} />
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
          Select a futures partner set to manage the accounts attached to this
          chart.
        </div>
      )}
    </div>
  );
}

function PartnerTile({
  label,
  account,
}: {
  label: string;
  account: PairListItem["longAccount"];
}): ReactNode {
  return (
    <div className="rounded-lg border border-border/60 bg-background/50 p-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 truncate text-sm font-medium text-foreground">
        {account.firmName}
      </p>
      <p className="mt-1 font-mono text-xs text-muted-foreground">
        {account.platformType} · {account.phase}
      </p>
    </div>
  );
}

/** Order-panel placeholder for the no-pair state. */
function NoPairOrderPanelHint({ loading }: { loading: boolean }): ReactNode {
  return (
    <div className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-card/50 px-6 py-8 text-center">
      {loading ? (
        <Loader2 aria-label="Loading pair" className="size-5 animate-spin text-accent" />
      ) : (
        <>
          <Monitor aria-hidden="true" className="size-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">
            Order Panel Disabled
          </p>
          <p className="max-w-sm text-xs text-muted-foreground">
            Pick a pair from the rail on the right to enable trading. Without a
            pair the chart and watchlist still stream live so you can preview
            symbols before opening one.
          </p>
        </>
      )}
    </div>
  );
}

/** Positions + pending placeholder for the no-pair state. */
function NoPairTablesHint(): ReactNode {
  return (
    <div className="flex flex-col items-center gap-1 rounded-xl border border-dashed border-border bg-card/40 px-6 py-6 text-center">
      <p className="text-xs text-muted-foreground">
        Positions and Pending Orders will appear here once a pair is selected.
      </p>
    </div>
  );
}
